import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GitCompare, ChevronDown, ChevronUp, TrendingUp, Bookmark, Table, BarChart3, Package } from 'lucide-react';
import DataTable from './DataTable';
import GridView from './GridView';
import PriceTimeline from './PriceTimeline';
import AnalysisMatrix from './AnalysisMatrix';
import SaveComparisonGroupModal from './SaveComparisonGroupModal';
import ComparisonGroupsPanel from './ComparisonGroupsPanel';
import EditGroupProductsModal from './EditGroupProductsModal';
import SkuComparisonLoader from './SkuComparisonLoader';
import { useSavedComparisonGroups, SavedComparisonGroup } from '../hooks/useSavedComparisonGroups';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

interface CompararProps {
  comparisonProducts: any[];
  onRemoveProduct: (productId: string) => void;
  onClearAll: () => void;
  onLoadComparisonProducts: (products: any[]) => void;
  allProducts?: any[]; // Productos disponibles para agregar a grupos
  currentTableProducts?: any[]; // Productos disponibles en la toma actual
  currentTableInfo?: { name: string; date?: string }; // Información de la toma actual
  tables?: any[]; // Lista de tomas disponibles
  selectedTable?: string; // Toma seleccionada actualmente
  onTableChange?: (tableName: string) => void; // Función para cambiar toma
  getTableDisplayName?: (tableName: string) => string; // Función para obtener nombre de toma
  getTableDropdownName?: (tableName: string) => string; // Función para obtener nombre dropdown
  clientId?: string;
}

const Comparar: React.FC<CompararProps> = ({ 
  comparisonProducts, 
  onRemoveProduct, 
  onClearAll,
  onLoadComparisonProducts,
  allProducts = [],
  currentTableProducts = [],
  currentTableInfo,
  tables = [],
  selectedTable,
  onTableChange,
  getTableDisplayName,
  getTableDropdownName,
  clientId
}) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const {
    savedGroups,
    loading: groupsLoading,
    error: groupsError,
    disabled: groupsDisabled,
    saveComparisonGroup,
    updateComparisonGroup,
    deleteComparisonGroup,
    loadComparisonGroup
  } = useSavedComparisonGroups();
  
  // Función wrapper con logs para debugging
  const handleRemoveProduct = (productId: string) => {
    onRemoveProduct(productId);
  };
  
  // Estado para tipo de vista (tabla o grilla) con memoria en localStorage
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() => {
    try {
      const savedViewMode = localStorage.getItem('userViewPreference');
      return (savedViewMode === 'table' || savedViewMode === 'grid') ? savedViewMode : 'table';
    } catch (error) {
      return 'table';
    }
  });

  // Estado para mostrar/ocultar la sección de análisis
  const [showAnalysis, setShowAnalysis] = useState<boolean>(() => {
    try {
      const savedAnalysisState = localStorage.getItem('showPriceAnalysis');
      return savedAnalysisState === 'true';
    } catch (error) {
      return false;
    }
  });

  // Estado para el tab activo (timeline o matrix)
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<'timeline' | 'matrix'>(() => {
    try {
      const savedTab = localStorage.getItem('activeAnalysisTab');
      return savedTab === 'matrix' ? 'matrix' : 'timeline';
    } catch (error) {
      return 'timeline';
    }
  });

  // Estado para los datos de precio histórico (compartido entre timeline y matrix)
  const [priceHistoryData, setPriceHistoryData] = useState<any>(null);
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);

  // Estados para la gestión de grupos de comparación
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showGroupsPanel, setShowGroupsPanel] = useState(false);
  const [showSkuPanel, setShowSkuPanel] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SavedComparisonGroup | null>(null);
  const [showEditProductsModal, setShowEditProductsModal] = useState(false);
  const [editingGroupProducts, setEditingGroupProducts] = useState<SavedComparisonGroup | null>(null);

  // Función para cambiar vista y guardar en localStorage
  const handleViewModeChange = (newViewMode: 'table' | 'grid') => {
    setViewMode(newViewMode);
    try {
      localStorage.setItem('userViewPreference', newViewMode);
    } catch (error) {
      // Error silenciado
    }
  };

  // Función para cambiar visibilidad de la sección de análisis
  const handleAnalysisToggle = () => {
    const newState = !showAnalysis;
    setShowAnalysis(newState);
    try {
      localStorage.setItem('showPriceAnalysis', newState.toString());
    } catch (error) {
      // Error silenciado
    }
  };

  // Función para cambiar el tab activo
  const handleTabChange = (tab: 'timeline' | 'matrix') => {
    setActiveAnalysisTab(tab);
    try {
      localStorage.setItem('activeAnalysisTab', tab);
    } catch (error) {
      // Error silenciado
    }
  };

  // Funciones para manejar grupos de comparación
  const handleSaveGroup = async (name: string, description: string, isPublic: boolean) => {
    try {
      await saveComparisonGroup(name, comparisonProducts, isPublic, description);
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateGroup = async (name: string, description: string, isPublic: boolean) => {
    if (!editingGroup?.id) return;
    
    try {
      await updateComparisonGroup(editingGroup.id, {
        name,
        description,
        isPublic,
        products: comparisonProducts
      });
      setEditingGroup(null);
    } catch (error) {
      throw error;
    }
  };

  const handleLoadGroup = (groupId: string) => {
    const products = loadComparisonGroup(groupId);
    
    if (products.length > 0) {
      onLoadComparisonProducts(products);
    } else {
      console.warn('⚠️ No products found in group or products array is empty');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (window.confirm(t('comparison_groups.confirm_delete_group'))) {
      try {
        await deleteComparisonGroup(groupId);
      } catch (error) {
        // Error silenciado
      }
    }
  };

  const handleEditGroup = (group: SavedComparisonGroup) => {
    setEditingGroup(group);
    setShowSaveModal(true);
  };

  const handleEditGroupProducts = (group: SavedComparisonGroup) => {
    setEditingGroupProducts(group);
    setShowEditProductsModal(true);
  };

  // Handler para cargar productos desde un SKU
  const handleLoadSkuProducts = (products: any[]) => {
    if (products.length > 0) {
      onLoadComparisonProducts(products);
      setShowSkuPanel(false); // Cerrar panel después de cargar
    }
  };

  const handleUpdateGroupProducts = async (products: any[]) => {
    if (!editingGroupProducts?.id) return;
    
    try {
      await updateComparisonGroup(editingGroupProducts.id, {
        name: editingGroupProducts.name,
        description: editingGroupProducts.description,
        isPublic: editingGroupProducts.isPublic,
        products: products
      });
      setEditingGroupProducts(null);
      setShowEditProductsModal(false);
    } catch (error) {
      throw error;
    }
  };

  // Función para cargar datos de precio histórico - OPTIMIZADA con endpoint batch
  const loadPriceHistory = async () => {
    if (comparisonProducts.length === 0) {
      setPriceHistoryData(null);
      return;
    }

    setLoadingPriceHistory(true);
    try {
      // Extraer todos los productIds - IMPORTANTE: usar solo id_catalogo real, no comparisonId
      const productIds = comparisonProducts
        .map(product => {
          // Priorizar id_catalogo real sobre comparisonId que tiene timestamp
          const id = product.id_catalogo || product.ID_CATALOGO || product.id;
          return id;
        })
        .filter(id => id);

      if (productIds.length === 0) {
        setPriceHistoryData(null);
        return;
      }

      // Una sola llamada al endpoint batch
      const response = await api.post('/api/products/price-history-batch', { productIds });

      const { productHistories, tomas } = response.data;

      setPriceHistoryData({
        productHistories: productHistories || {},
        tomas: tomas || []
      });

    } catch (error) {
      console.error('Error in loadPriceHistory:', error);
      setPriceHistoryData(null);
    } finally {
      setLoadingPriceHistory(false);
    }
  };

  // Crear una key estable basada en los IDs de productos para evitar re-renders innecesarios
  const comparisonProductsKey = comparisonProducts
    .map(p => p.comparisonId || p.id_catalogo || p.ID_CATALOGO || p.id)
    .filter(id => id)
    .sort()
    .join(',');

  // Efecto para cargar datos cuando cambian los productos de comparación
  useEffect(() => {
    if (comparisonProducts.length > 0) {
      loadPriceHistory();
    } else {
      // Limpiar datos cuando no hay productos
      setPriceHistoryData(null);
    }
  }, [comparisonProductsKey]); // Usar la key en lugar del array completo

  return (
    <div className="space-y-6">
      {/* Sección de toma actual - Solo visible cuando hay productos en comparación */}
      {comparisonProducts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              {getTableDisplayName && selectedTable ? getTableDisplayName(selectedTable) : (currentTableInfo?.name || 'Data')}
            </h2>
            
            {/* Indicador conectado y selector de tomas */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                {t('dashboard.connected')}
              </div>
              
              {tables.length > 0 && selectedTable && onTableChange && getTableDropdownName ? (
                <div className="flex items-center space-x-2">
                  <Table className="h-4 w-4 text-gray-500" />
                  <select
                    value={selectedTable}
                    onChange={(e) => onTableChange(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-48"
                  >
                    <option value="">{t('dashboard.select_data_set')}</option>
                    {tables.map((toma, index) => (
                      <option key={index} value={toma.tableName}>
                        {getTableDropdownName(toma.tableName)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Table className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    {currentTableInfo?.date || currentTableInfo?.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header con botones de gestión de grupos - Siempre visible */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            {comparisonProducts.length > 0 
              ? t('compare.comparison_panel')
              : t('comparison_groups.title')
            }
          </h2>
          
          <div className="flex items-center space-x-2">
            {currentUser && !groupsDisabled && (
              <>
                <button
                  onClick={() => setShowSaveModal(true)}
                  disabled={comparisonProducts.length === 0}
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('comparison_groups.save_current_comparison')}
                >
                  <Bookmark className="h-4 w-4" />
                  <span>{t('comparison_groups.save_group')}</span>
                </button>
                
                <button
                  onClick={() => setShowGroupsPanel(!showGroupsPanel)}
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                  title={t('comparison_groups.manage_groups')}
                >
                  <span>{t('comparison_groups.title')}</span>
                  {showGroupsPanel ? (
                    <ChevronUp className="h-4 w-4 text-gray-400 ml-1" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400 ml-1" />
                  )}
                </button>

                {/* Botón para cargar SKUs */}
                {selectedTable && (
                  <button
                    onClick={() => setShowSkuPanel(!showSkuPanel)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 border border-indigo-200 rounded-lg transition-colors"
                    title={t('sku_comparison.load_from_sku')}
                  >
                    <Package className="h-4 w-4" />
                    <span>{t('sku_comparison.load_sku')}</span>
                    {showSkuPanel ? (
                      <ChevronUp className="h-4 w-4 text-indigo-400 ml-1" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-indigo-400 ml-1" />
                    )}
                  </button>
                )}
              </>
            )}

            {/* Mensaje cuando está deshabilitado */}
            {groupsDisabled && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                <span className="text-sm text-orange-700">
                  {t('comparison_groups.feature_disabled')}
                </span>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Panel de grupos guardados (colapsable) - Con animación */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          showGroupsPanel && !groupsDisabled
            ? 'max-h-[2000px] opacity-100'
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <ComparisonGroupsPanel
          groups={savedGroups}
          loading={groupsLoading}
          error={groupsError}
          onLoadGroup={handleLoadGroup}
          onEditGroup={handleEditGroup}
          onEditGroupProducts={handleEditGroupProducts}
          onDeleteGroup={handleDeleteGroup}
          currentUser={currentUser}
        />
      </div>

      {/* Panel de SKUs (colapsable) - Con animación */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          showSkuPanel && selectedTable
            ? 'max-h-[2000px] opacity-100'
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        {selectedTable && (
          <SkuComparisonLoader
            tableName={selectedTable}
            onLoadSkuProducts={handleLoadSkuProducts}
          />
        )}
      </div>

      {/* Productos en comparación */}
      {comparisonProducts.length > 0 ? (
        <div className="space-y-4">
          
          {/* Renderizar vista apropiada - Product Comparison */}
          {viewMode === 'table' ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <DataTable 
                tableName="comparison" 
                hideFilters={true}
                comparisonMode={true}
                comparisonData={comparisonProducts}
                onRemoveFromComparison={handleRemoveProduct}
                displayName=""
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                onClearAll={onClearAll}
                currentTableProducts={currentTableProducts}
                currentTableInfo={currentTableInfo}
                clientId={clientId}
              />
            </div>
          ) : (
            <GridView 
              tableName="comparison" 
              hideFilters={true}
              comparisonMode={true}
              comparisonData={comparisonProducts}
              onRemoveFromComparison={handleRemoveProduct}
              displayName=""
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              onClearAll={onClearAll}
              currentTableProducts={currentTableProducts}
              currentTableInfo={currentTableInfo}
            />
          )}

          {/* Sección de Análisis de Precios con Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Header colapsable */}
            <button
              onClick={handleAnalysisToggle}
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {t('compare.price_analysis')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('compare.price_analysis_subtitle')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {loadingPriceHistory && !showAnalysis && (
                  <div className="flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-lg">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                    <span className="text-sm text-blue-600">{t('compare.loading_price_history')}</span>
                  </div>
                )}
                <span className="text-sm text-gray-500">
                  {showAnalysis ? t('compare.hide_analysis') : t('compare.show_analysis')}
                </span>
                {showAnalysis ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {/* Contenido del análisis con tabs */}
            {showAnalysis && (
              <div className="px-6 pb-6">
                <div className="border-t border-gray-200 pt-6">
                  {/* Tabs */}
                  <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => handleTabChange('timeline')}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                        activeAnalysisTab === 'timeline'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <TrendingUp className="h-4 w-4" />
                      <span className="font-medium">{t('compare.price_timeline')}</span>
                    </button>
                    <button
                      onClick={() => handleTabChange('matrix')}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                        activeAnalysisTab === 'matrix'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span className="font-medium">{t('compare.analysis_matrix')}</span>
                    </button>
                  </div>

                  {/* Contenido según el tab activo */}
                  {activeAnalysisTab === 'timeline' ? (
                    <PriceTimeline
                      comparisonProducts={comparisonProducts}
                      shouldLoad={false}
                      priceHistoryData={priceHistoryData}
                      loading={loadingPriceHistory}
                    />
                  ) : (
                    <AnalysisMatrix
                      comparisonProducts={comparisonProducts}
                      shouldLoad={false}
                      priceHistoryData={priceHistoryData}
                      loading={loadingPriceHistory}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Estado vacío
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <GitCompare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">
            {t('compare.no_products')}
          </h3>
          <p className="text-gray-600">
            {t('compare.no_products_help')}
          </p>
        </div>
      )}

      {/* Modales */}
      {!groupsDisabled && (
        <>
          <SaveComparisonGroupModal
            isOpen={showSaveModal}
            onClose={() => {
              setShowSaveModal(false);
              setEditingGroup(null);
            }}
            onSave={editingGroup ? handleUpdateGroup : handleSaveGroup}
            products={comparisonProducts}
            initialData={editingGroup ? {
              name: editingGroup.name,
              description: editingGroup.description,
              isPublic: editingGroup.isPublic
            } : undefined}
            isEditing={!!editingGroup}
          />
          
          <EditGroupProductsModal
            isOpen={showEditProductsModal}
            onClose={() => {
              setShowEditProductsModal(false);
              setEditingGroupProducts(null);
            }}
            onSave={handleUpdateGroupProducts}
            groupName={editingGroupProducts?.name || ''}
            initialProducts={editingGroupProducts?.products || []}
            availableProducts={allProducts}
          />
        </>
      )}
    </div>
  );
};

export default Comparar;
